// frontend/src/features/swimmers/components/RegisterSwimmerForm/RegisterSwimmerForm.tsx

import React, { useMemo } from "react";
import clsx from "clsx";
import { FormField } from "../../../../shared/components/forms/FormField";
import { Button } from "../../../../shared/components/ui/Button";
import { Card } from "../../../../shared/components/ui/Card";
import { PlusIcon, TrashIcon } from "lucide-react";
import type {
  SwimmerBatchItem,
  Swimmer,
} from "../../../../api/client/types/swimmer.types";
import { useRegisterSwimmerForm } from "./useRegisterSwimmerForm";
import type { AgeClassification } from "../../../../api/client/types/age-classification.types";
import type { SwimmerType } from "../../../../api/client/types/swimmer-type.types";

interface RegisterSwimmerFormProps {
  ageClassifications: AgeClassification[];
  swimmerTypes: SwimmerType[];
  onSubmit: (data: SwimmerBatchItem[]) => void;
  isSubmitting?: boolean;
  initialSwimmer?: Swimmer | null;
}

export const RegisterSwimmerForm: React.FC<RegisterSwimmerFormProps> = ({
  ageClassifications,
  swimmerTypes,
  onSubmit,
  isSubmitting = false,
  initialSwimmer,
}) => {
  const swimmerTypesOptions = useMemo(
    () =>
      swimmerTypes.map((swimmerType) => ({
        label: swimmerType.name,
        value: swimmerType.id.toString(),
      })),
    [swimmerTypes]
  );

  const {
    register,
    handleSubmit,
    control,
    errors,
    isValid,
    fields,
    watchedSwimmers,
    ageClassificationsOptions,
    getAdultOptions,
    isMinorUnder12,
    addSwimmer,
    removeSwimmer,
    isPreloadedSwimmer,
  } = useRegisterSwimmerForm({
    onSubmit,
    ageClassifications,
    initialSwimmer,
  });

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6 w-full max-w-4xl mx-auto"
    >
      {/* Informational Note */}
      <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <span className="font-semibold">Nota:</span> Para registrar un menor
          de 12 años, debe crear al menos un adulto en el grupo.
        </p>
      </div>

      {/* Swimmers */}
      <div className="flex flex-col gap-6">
        {fields.map((field, index) => {
          const adultOptions = getAdultOptions(index);
          const acId = watchedSwimmers[index]?.age_classification_id || "";
          const isMinor = isMinorUnder12(acId);
          const isPreloaded = isPreloadedSwimmer(index);

          return (
            <Card
              key={field.id}
              variant="formSection"
              className={isPreloaded ? "bg-gray-100 border-gray-200" : ""}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h2
                    className={`text-lg font-medium ${
                      isPreloaded ? "text-gray-500" : "text-gray-800"
                    }`}
                  >
                    Bañista {index + 1}
                  </h2>
                  {isPreloaded && (
                    <span className="text-xs px-2 py-1 bg-gray-200 text-gray-600 rounded font-medium">
                      Pre-cargado
                    </span>
                  )}
                </div>

                {fields.length > 1 && !isPreloaded && (
                  <TrashIcon
                    className="w-5 h-5 text-red-600 hover:text-red-700"
                    onClick={() => removeSwimmer(index)}
                  />
                )}
              </div>

              {/* Name - First field */}
              <div className="mb-4">
                <FormField
                  type="input"
                  label="Nombre y Apellido"
                  required
                  inputProps={{
                    ...register(`swimmers.${index}.name`),
                    type: "text",
                    placeholder: "Nombre completo",
                    disabled: isPreloaded,
                    className: clsx(
                      "border rounded-md p-2 w-full text-sm",
                      isPreloaded &&
                        "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200"
                    ),
                  }}
                  error={errors.swimmers?.[index]?.name?.message}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Torre and Apto: editable for each swimmer, prefilled from first; disabled for preloaded swimmer */}
                <FormField
                  type="input"
                  label="Torre"
                  required
                  inputProps={{
                    ...register(`swimmers.${index}.building_tower`),
                    type: "text",
                    placeholder: "Ej: A, B, C",
                    disabled: isPreloaded,
                    className: clsx(
                      "border rounded-md p-2 w-full text-sm",
                      isPreloaded &&
                        "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200"
                    ),
                  }}
                  error={errors.swimmers?.[index]?.building_tower?.message}
                />

                <FormField
                  type="input"
                  label="Apto"
                  required
                  inputProps={{
                    ...register(`swimmers.${index}.building_apartment`),
                    type: "text",
                    placeholder: "Ej: 101, 202",
                    disabled: isPreloaded,
                    className: clsx(
                      "border rounded-md p-2 w-full text-sm",
                      isPreloaded &&
                        "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200"
                    ),
                  }}
                  error={errors.swimmers?.[index]?.building_apartment?.message}
                />
              </div>

              {/* Age Classification - Full width row */}
              <div className="mb-4">
                <FormField
                  type="radioGroup"
                  label="Clasificación de Edad"
                  required
                  radioGroupProps={{
                    control,
                    name: `swimmers.${index}.age_classification_id`,
                    options: ageClassificationsOptions,
                    disabled: isPreloaded,
                    layout: "horizontal",
                  }}
                  error={
                    errors.swimmers?.[index]?.age_classification_id?.message
                  }
                />
              </div>

              {/* Swimmer Type - Full width row */}
              <div className="mb-4">
                <FormField
                  type="radioGroup"
                  label="Tipo de Bañista"
                  required
                  radioGroupProps={{
                    control,
                    name: `swimmers.${index}.swimmer_type_id`,
                    options: swimmerTypesOptions,
                    disabled: isPreloaded,
                    layout: "horizontal",
                  }}
                  error={errors.swimmers?.[index]?.swimmer_type_id?.message}
                />
              </div>

              {/* RESPONSABLE — Only visible when minor */}
              {isMinor && (
                <div className="mb-4">
                  <FormField
                    type="select"
                    label="Responsable"
                    required
                    selectProps={{
                      ...register(`swimmers.${index}.responsibleIdx`),

                      // Always include empty placeholder first, then adult options
                      options:
                        adultOptions.length > 0
                          ? [
                              {
                                value: "",
                                label: "Seleccione un responsable",
                              },
                              ...adultOptions,
                            ]
                          : [
                              {
                                value: "",
                                label: "No hay adultos disponibles aún",
                              },
                            ],

                      // Disable if:
                      // - swimmer is preloaded
                      // - no adults exist yet
                      disabled: isPreloaded || adultOptions.length === 0,
                      className: clsx(
                        "text-sm",
                        (isPreloaded || adultOptions.length === 0) &&
                          "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200"
                      ),
                    }}
                    error={errors.swimmers?.[index]?.responsibleIdx?.message}
                  />

                  {/* Helpful message when there are no adults yet */}
                  {adultOptions.length === 0 && (
                    <p className="text-gray-600 text-xs italic mt-1">
                      Debe agregar al menos un adulto para seleccionar un
                      responsable.
                    </p>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Add Swimmer Button */}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={addSwimmer}
          disabled={fields.length >= 10}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5"
        >
          <PlusIcon className="w-3.5 h-3.5" />
          Agregar Bañista
        </Button>
      </div>

      {fields.length >= 10 && (
        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-sm text-yellow-800">
            Has alcanzado el límite máximo de 10 bañistas por formulario.
          </p>
        </div>
      )}

      {/* Zod-level array errors */}
      {errors.swimmers && typeof errors.swimmers.message === "string" && (
        <div className="text-red-600 text-sm">{errors.swimmers.message}</div>
      )}
      {errors.root?.global && (
        <div className="text-red-600 text-sm mt-2">
          {errors.root.global.message}
        </div>
      )}

      {/* Submit */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 mt-2">
        <Button
          type="submit"
          disabled={!isValid || isSubmitting}
          className="text-sm px-8 py-3 font-semibold"
        >
          {isSubmitting ? "Registrando..." : "Registrar Bañistas"}
        </Button>
      </div>
    </form>
  );
};
